import type { PlannerTask } from './weekly_planner';
export interface CalendarEvent {
    id: number;
    title: string;
    event_date: string;
    start_time: string;
    end_time: string;
}
export type CalendarTask = PlannerTask & {
    calendarOriginal?: PlannerTask;
    calendarRecoveryFor?: string;
    calendarWeek?: string;
    calendarEvent?: boolean;
};
const minutes = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
const clock = (n: number) =>
    `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
export function weekStart(
    date = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Kyiv' })
): string {
    const d = new Date(`${date}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    return d.toISOString().slice(0, 10);
}
export function dateOffset(date: string, days: number): string {
    const d = new Date(`${date}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}
// Derived blocks never replace the weekly template. Keep dated recovery placements separately.
export function calendarBase(input: CalendarTask[]): CalendarTask[] {
    const originals = new Map<string, CalendarTask>();
    const result: CalendarTask[] = [];
    for (const t of input) {
        if (t.calendarOriginal) {
            originals.set(t.calendarOriginal.id, t.calendarOriginal);
            continue;
        }
        if (t.calendarEvent) continue;
        if (t.calendarRecoveryFor && (t.dayIndex < 0 || t.status === 'needs_reschedule')) continue;
        result.push(t);
    }
    for (const t of originals.values()) if (!result.some((x) => x.id === t.id)) result.push(t);
    return result;
}
export function projectCalendar(
    input: CalendarTask[],
    events: CalendarEvent[],
    monday: string
): CalendarTask[] {
    const base = calendarBase(input),
        result: CalendarTask[] = [];
    const recovery = base.filter((t) => t.calendarRecoveryFor && t.calendarWeek === monday);
    const active = events.filter(
        (e) => e.event_date >= monday && e.event_date <= dateOffset(monday, 6)
    );
    for (const task of base.filter((t) => !t.calendarRecoveryFor)) {
        const blockers = active.filter((e) => e.event_date === dateOffset(monday, task.dayIndex));
        if (
            task.category !== 'delivery' ||
            task.dayIndex < 0 ||
            task.status === 'completed' ||
            task.status === 'needs_reschedule'
        ) {
            result.push(task);
            continue;
        }
        let intervals: [[number, number]] | [number, number][] = [
            [minutes(task.startTime), minutes(task.endTime)],
        ];
        for (const e of blockers)
            intervals = intervals.flatMap(([s, f]) => {
                const a = minutes(e.start_time),
                    b = minutes(e.end_time);
                return b <= s || a >= f
                    ? [[s, f]]
                    : ([
                          [s, Math.min(f, a)],
                          [Math.max(s, b), f],
                      ].filter(([x, y]) => y > x) as [number, number][]);
            });
        const remaining = intervals.reduce((sum, [s, f]) => sum + f - s, 0),
            blocked = task.durationMins - remaining;
        if (blocked <= 0) {
            result.push(task);
            continue;
        }
        intervals.forEach(([s, f], i) =>
            result.push({
                ...task,
                id: `${task.id}_cal_${i}`,
                startTime: clock(s),
                endTime: clock(f),
                durationMins: f - s,
                isFixed: true,
                calendarOriginal: task,
            })
        );
        // An invisible original marker also preserves a fully blocked template task.
        if (!intervals.length)
            result.push({
                ...task,
                id: `${task.id}_cal_origin`,
                dayIndex: -2,
                status: 'planned',
                durationMins: 0,
                calendarOriginal: task,
            });
        const placed = recovery
            .filter((t) => t.calendarRecoveryFor === task.id)
            .flatMap((t) => {
                let parts: [number, number][] = [[minutes(t.startTime), minutes(t.endTime)]];
                for (const event of active.filter(
                    (e) => e.event_date === dateOffset(monday, t.dayIndex)
                )) {
                    const a = minutes(event.start_time),
                        b = minutes(event.end_time);
                    parts = parts.flatMap(([s, f]) =>
                        b <= s || a >= f
                            ? [[s, f]]
                            : ([
                                  [s, Math.min(f, a)],
                                  [Math.max(s, b), f],
                              ].filter(([x, y]) => y > x) as [number, number][])
                    );
                }
                if (
                    parts.length === 1 &&
                    parts[0][0] === minutes(t.startTime) &&
                    parts[0][1] === minutes(t.endTime)
                )
                    return [t];
                return parts.map(([s, f], i) => ({
                    ...t,
                    id: t.id + '_part_' + i,
                    startTime: clock(s),
                    endTime: clock(f),
                    durationMins: f - s,
                }));
            });
        let availableRecovery = blocked;
        const capped = placed.flatMap((t) => {
            const duration = Math.min(availableRecovery, t.durationMins);
            availableRecovery -= duration;
            return duration > 0
                ? [
                      {
                          ...t,
                          durationMins: duration,
                          endTime: clock(minutes(t.startTime) + duration),
                      },
                  ]
                : [];
        });
        result.push(...capped);
        const debt = Math.max(0, blocked - capped.reduce((sum, t) => sum + t.durationMins, 0));
        if (debt)
            result.push({
                ...task,
                id: `${task.id}_cal_debt_${placed.length}`,
                title: `${task.title} · перенесено через подію`,
                dayIndex: -1,
                status: 'needs_reschedule',
                durationMins: debt,
                isFixed: false,
                calendarRecoveryFor: task.id,
                calendarWeek: monday,
            });
    }
    // Keep other weeks' placements for later navigation, outside the visible timeline.
    for (const t of base.filter((t) => t.calendarRecoveryFor && t.calendarWeek !== monday))
        result.push({
            ...t,
            id: `${t.id}_hidden`,
            dayIndex: -2,
            durationMins: 0,
            status: 'planned',
            calendarOriginal: t,
        });
    for (const e of active)
        result.push({
            id: `calendar_${e.id}`,
            title: e.title.replace(
                /[&<>"']/g,
                (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!
            ),
            dayIndex: Math.round((Date.parse(e.event_date) - Date.parse(monday)) / 86400000),
            startTime: e.start_time.slice(0, 5),
            endTime: e.end_time.slice(0, 5),
            durationMins: minutes(e.end_time) - minutes(e.start_time),
            category: 'other',
            isFixed: true,
            allowSplit: false,
            priority: 'high',
            status: 'planned',
            calendarEvent: true,
        });
    return result.filter((t) => t.dayIndex !== -2 || !!t.calendarOriginal);
}
